import { motion } from 'framer-motion';
import { FileText, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="bg-charcoal text-charcoal-foreground py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-primary text-sm mb-4">
              <Link to={ROUTE_PATHS.HOME} className="hover:text-primary/80 transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span>Terms of Service</span>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <FileText className="text-primary" size={24} />
              </div>
              <span className="text-primary text-xs font-semibold tracking-widest uppercase">Legal Document II / III</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-charcoal-foreground/70 text-lg">
              SHONCHIN MANOR &amp; CO. — Your rights and responsibilities when using our website.
            </p>
            <p className="text-charcoal-foreground/50 text-sm mt-3">Effective date: 2026 · © 2026 SHONCHIN MANOR &amp; CO.</p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="bg-card border border-border rounded-2xl p-8 mb-8 text-sm text-muted-foreground leading-relaxed">
            These Terms of Service constitute a legal agreement between you and SHONCHIN MANOR &amp; CO. ("the Company", "we" or "us") governing your access to and use of our website. Please read them carefully. By accessing or continuing to use the website, you acknowledge that you have read, understood and agree to be bound by these Terms.
          </div>

          <Section number="1" title="Acceptance and Changes">
            <p>Your use of the website constitutes acceptance of these Terms. If you are using the website on behalf of an entity, you warrant that you have authority to bind it. We may amend these Terms from time to time; amendments take effect upon publication on the website. Where changes are material, we will provide conspicuous notice.</p>
          </Section>

          <Section number="2" title="The Company and Services">
            <p>SHONCHIN MANOR &amp; CO. is a professional firm registered in Dubai, United Arab Emirates, providing strategic planning, management advisory and business-planning services. The website is our official platform for corporate presentation and client liaison.</p>
            <p>The website itself does not constitute an offer to enter into any service contract, nor does accessing it create an advisory, fiduciary or other contractual relationship between you and the Company. Any formal engagement must be established through a separately signed written engagement letter.</p>
          </Section>

          <Section number="3" title="Eligibility">
            <p>You must have full legal capacity — and have reached the age of majority in your jurisdiction — to use the website. If you access the website on behalf of an organisation, you represent that you are duly authorised to do so.</p>
          </Section>

          <Section number="4" title="Acceptable Use">
            <p>In using the website, you agree that you will not:</p>
            <ul>
              <li>violate any applicable law or regulation;</li>
              <li>use the website for any unlawful purpose or to infringe the rights of others;</li>
              <li>attempt any form of attack, intrusion, scraping, reverse engineering or other unauthorised access;</li>
              <li>upload or transmit content containing viruses or malicious code;</li>
              <li>impersonate any person or submit false information;</li>
              <li>use the website to send unsolicited commercial communications.</li>
            </ul>
          </Section>

          <Section number="5" title="Intellectual Property">
            <p>All content on the website — including text, images, logos, layout and software code — is the property of the Company or its respective rightsholders and is protected by applicable law and international treaties. "SHONCHIN MANOR", "SHONCHIN" and the Company's graphical identity are commercial marks of the Company.</p>
            <p>Other than personal, lawful and non-commercial browsing as permitted by these Terms, you may not reproduce, publish, redistribute, modify, translate, reverse-engineer or otherwise exploit the content of the website without our prior written consent.</p>
          </Section>

          <Section number="6" title="Nature of Content and Disclaimer">
            <p>All content on the website — including articles, case descriptions and opinions — is provided for general information and reference only, and does not constitute professional advice, including legal, financial or investment advice, on any specific matter. It has not been prepared having regard to your particular circumstances.</p>
            <p>Any decision taken in reliance on information published on the website is made at your own discretion and risk. Before making any decision with material consequences, we recommend seeking advice from a duly qualified professional.</p>
            <p>The Company makes no express or implied warranty as to the accuracy, completeness or timeliness of information on the website. The website may contain links to third-party websites; the Company assumes no responsibility for their content or privacy practices.</p>
          </Section>

          <Section number="7" title="Availability and Force Majeure">
            <p>We will use reasonable efforts to keep the website operational, but do not warrant that it will be uninterrupted or error-free. We may suspend or modify the website for maintenance, upgrade or other legitimate reasons. The Company shall not be liable for any interruption caused by war, natural disaster, governmental action, change of law, power or telecommunications failure, cyber-attack, public-health event, or any other event beyond our reasonable control.</p>
          </Section>

          <Section number="8" title="Limitation of Liability">
            <p>To the maximum extent permitted by applicable law, the Company and its directors, employees, agents and affiliates shall not be liable for: (a) any indirect, incidental, special, punitive or consequential damages; (b) any loss of profit, goodwill or business opportunity; (c) any loss or corruption of data, or system interruption; or (d) any loss arising from decisions made in reliance on information published on the website.</p>
            <p>In no event shall the Company's aggregate liability under these Terms or arising from your use of the website exceed one hundred US dollars (US$100) or its equivalent, save to the extent that mandatory provisions of applicable law do not permit such limitation — for example, liability arising from our wilful misconduct or gross negligence.</p>
          </Section>

          <Section number="9" title="Indemnification">
            <p>You agree to indemnify and hold harmless the Company, its affiliates, directors, employees and agents from and against any third-party claim, action, proceeding, penalty or loss — including reasonable legal fees — arising from your breach of these Terms, violation of applicable law, or infringement of any third-party right.</p>
          </Section>

          <Section number="10" title="Termination">
            <p>You may cease using the website at any time. The Company may, at any time and without prior notice, refuse your continued access in whole or in part, including where you breach these Terms or on other reasonable grounds. Sections 5, 6, 8, 9 and 11 shall survive termination of these Terms.</p>
          </Section>

          <Section number="11" title="Governing Law and Disputes">
            <p>These Terms, and any dispute arising out of or in connection with them or your use of the website, shall be governed by the laws of the Emirate of Dubai and the federal laws of the United Arab Emirates applicable therein, excluding their conflict-of-laws rules.</p>
            <p>Any dispute shall first be resolved through good-faith negotiation. Failing settlement, disputes shall be submitted to the competent courts of the Emirate of Dubai, or — where the parties so agree in writing — to final and binding arbitration administered by the Dubai International Arbitration Centre (DIAC) in accordance with its then-current rules, with Dubai as the seat and English as the language of arbitration.</p>
          </Section>

          <Section number="12" title="Miscellaneous">
            <ul>
              <li><strong>Entire Agreement:</strong> these Terms, together with the Privacy Policy and Compliance Statement, constitute the entire agreement between you and the Company regarding your use of the website.</li>
              <li><strong>Severability:</strong> if any provision is held invalid or unenforceable, the remaining provisions shall continue in full force.</li>
              <li><strong>No Waiver:</strong> no failure or delay by the Company in exercising any right shall operate as a waiver.</li>
              <li><strong>Assignment:</strong> you may not assign any right or obligation under these Terms without our written consent; we may assign to an affiliate or business successor.</li>
            </ul>
          </Section>
        </motion.div>

        {/* Footer nav between legal docs */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row gap-4 justify-between">
          <Link to={ROUTE_PATHS.PRIVACY} className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium">
            <ChevronRight size={16} /> Privacy Policy
          </Link>
          <Link to={ROUTE_PATHS.COMPLIANCE} className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium">
            <ChevronRight size={16} /> Compliance Statement
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0">
          {number}
        </span>
        {title}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-3 pl-11">
        {children}
      </div>
    </div>
  );
}
